// TODO: Lots of param-reassigns in this file, consider refactoring

import type { ColormapName } from '@/src/utils/color';

import { Orientation } from '@src/store/dicom-web/classification-typing';
import { createColorScale } from '@src/utils/color';

import { abs } from 'mathjs';

import { defineStore } from 'pinia';

import { computeSaxRangeIntervals } from '@/src/components/pixel-overlay/useLocalizerGeometry';

import { AreaContourBase } from '../contours/area-contour-base';
import { useContourContainerStore } from '../contours/contour-container-store';

import {
  ContourLabel,
  encodeContourLabelItemId,
} from '../contours/contour-definitions';
import { isPointWithinPolygon } from '../contours/contour-processing/isPointWithinPolygon';
import { LineContour } from '../contours/line-contour';
import { PointContour } from '../contours/point-contour';
import { PolygonClosedContour } from '../contours/polygon-closed-contour';
import { PolygonOpenContour } from '../contours/polygon-open-contour';
import { Point } from '../contours/types/contour-definition-array-type';
import { useViewportStateStore } from '../contours/viewport-state-store';
import {
  ImageIndices,
  useCurrentStudyStore,
} from '../dicom-web/current-study-store';
import { seriesClassification } from '../dicom-web/SeriesClassification';

const MYOCARDIAL_DENSITY = 1.05;

// chord 60 hardcoded for now
export const CHORD_ENDO_OFFSET = 0;
export const CHORD_EPI_OFFSET = 100;
export const CHORD_NB = 60;

export const LAX_CHORD_NB = 61;

export enum FunctionAnalysisType {
  LaxMonoplane2CV = 'LaxMonoplane2CV',
  LaxMonoplane4CV = 'LaxMonoplane4CV',
  LaxBiplane = 'LaxBiplane',
  SaxStack = 'SaxStack',
  SaxStackPap = 'SaxStackPap',
}

export const ContourLabelToIcon: Partial<Record<ContourLabel, string>> = {
  [ContourLabel.SaxLvEpi]: 'green-circle',
  [ContourLabel.SaxLvOpenEpi]: 'green-open-circle',
  [ContourLabel.SaxLvEndo]: 'red-circle',
  [ContourLabel.SaxRvEndo]: 'yellow-circle',
  [ContourLabel.RvInsertion]: 'blue-circle',
  [ContourLabel.LaxLvEndo]: 'red-u',
  [ContourLabel.LaxLvEpi]: 'green-u',
  [ContourLabel.SaxLvPapillary]: 'magenta-dot',
  [ContourLabel.SaxLvExclusion]: 'blue-dot',
};

// ToDo papillary muscles
export interface FunctionAnalysisState {
  EF: number;
  SV: number;
  EDV: number;
  ESV: number;
  SVindH: number;
  EDVindH: number;
  ESVindH: number;
  SVindBSA: number;
  EDVindBSA: number;
  ESVindBSA: number;
  systolicMyoMass: number;
  diastolicMyoMass: number;
  systolicPapMass: number;
  diastolicPapMass: number;
  systolicRvPapVolume: number;
  diastolicRvPapVolume: number;
  systolicMyoMassindBSA: number;
  diastolicMyoMassindBSA: number;
  systolicMyoMassindH: number;
  diastolicMyoMassindH: number;
  RVEF: number;
  RVSV: number;
  RVEDV: number;
  RVESV: number;
  RVSVindH: number;
  RVEDVindH: number;
  RVESVindH: number;
  RVSVindBSA: number;
  RVEDVindBSA: number;
  RVESVindBSA: number;
  volumes: number[];
  excludedVolumes: number[];
  volumesWithCone: number[];
  rvVolumes: number[];
  rvPapVolumes: number[];
  triggerTimes: number[];
  myoMass: number[];
  papMass: number[];
  chords: number[][][];
  wallThickening: number[][]; // [slice][chord] – systole vs end-diastole (bull's-eye)
  wallThickeningPerImage: number[][][]; // [slice][phase][chord] – % vs end-diastole, per image
  lengthEndo: number[];
  lengthEpi: number[];
  basalConeVolumes: number[];
  areaEndo: number[];
  areaEpi: number[];
  SaxContourLengthEndo: number[][];
  SaxContourLengthEpi: number[][];
  SaxCFSEndoPerImage: number[][]; // [phase][slice] – % circumferential shortening vs end-diastole (endo)
  SaxCFSEpiPerImage: number[][]; // [phase][slice] – % circumferential shortening vs end-diastole (epi)
  LaxExtendLFSEndoPerImage: number[]; // [phase] – % extend-line length change vs ED (endo)
  LaxExtendLFSEpiPerImage: number[]; // [phase] – % extend-line length change vs ED (epi)
  LaxContourLengthEndo: number[];
  LaxContourLengthEpi: number[];
  LaxLFSEndoPerImage: number[]; // [phase] – % length change vs end-diastole (endo)
  LaxLFSEpiPerImage: number[]; // [phase] – % length change vs end-diastole (epi)
  laxChords: number[][]; // [phase][chord] – LAX center-chord lengths (LAX_CHORD_NB)
  laxWallThickening: number[]; // [chord] – systole vs end-diastole (%)
  laxWallThickeningPerImage: number[][]; // [phase][chord] – vs end-diastole (%)
  // GLS: global longitudinal strain
  GLSEndo: number;
  GLSEpi: number;
  // LFS: longitudinal fractional shortening
  LFSEndo: number;
  LFSEpi: number;
  // CFS: circumferential fractional shortening
  CFSEndo: number;
  CFSEpi: number;
  HeartRateMean: number;
  CO: number;
  CI: number;
  diastolicPhase: number;
  systolicPhase: number;
  diastolicPhaseRV: number;
  systolicPhaseRV: number;
  seriesIndices: ImageIndices;
  secSeriesIndices: ImageIndices;
  PatHeight: number;
  PatWeight: number;
  BSA: number;
  excludedImageUids: string[];
}

export function getDefaultFunctionAnalysisState(): FunctionAnalysisState {
  return {
    EF: 0,
    SV: 0,
    EDV: 0,
    ESV: Infinity,
    SVindH: 0,
    EDVindH: 0,
    ESVindH: 0,
    SVindBSA: 0,
    EDVindBSA: 0,
    ESVindBSA: 0,
    systolicMyoMass: 0,
    diastolicMyoMass: 0,
    systolicPapMass: 0,
    diastolicPapMass: 0,
    systolicMyoMassindBSA: 0,
    diastolicMyoMassindBSA: 0,
    systolicMyoMassindH: 0,
    diastolicMyoMassindH: 0,
    systolicRvPapVolume: 0,
    diastolicRvPapVolume: 0,
    RVEF: 0,
    RVSV: 0,
    RVEDV: 0,
    RVESV: Infinity,
    RVSVindH: 0,
    RVEDVindH: 0,
    RVESVindH: 0,
    RVSVindBSA: 0,
    RVEDVindBSA: 0,
    RVESVindBSA: 0,
    volumes: [],
    excludedVolumes: [],
    volumesWithCone: [],
    rvVolumes: [],
    rvPapVolumes: [],
    triggerTimes: [],
    myoMass: [],
    papMass: [],
    chords: [],
    wallThickening: [],
    wallThickeningPerImage: [],
    lengthEndo: [],
    lengthEpi: [],
    basalConeVolumes: [],
    areaEndo: [],
    areaEpi: [],
    SaxContourLengthEndo: [],
    SaxContourLengthEpi: [],
    SaxCFSEndoPerImage: [],
    SaxCFSEpiPerImage: [],
    LaxExtendLFSEndoPerImage: [],
    LaxExtendLFSEpiPerImage: [],
    LaxContourLengthEndo: [],
    LaxContourLengthEpi: [],
    LaxLFSEndoPerImage: [],
    LaxLFSEpiPerImage: [],
    laxChords: [],
    laxWallThickening: [],
    laxWallThickeningPerImage: [],
    GLSEndo: 0,
    GLSEpi: 0,
    LFSEndo: 0,
    LFSEpi: 0,
    CFSEndo: 0,
    CFSEpi: 0,
    HeartRateMean: 0,
    CO: 0,
    CI: 0,
    diastolicPhase: -1,
    systolicPhase: -1,
    diastolicPhaseRV: -1,
    systolicPhaseRV: -1,
    seriesIndices: { seriesIdx: -1, sliceIdx: -1, imageIdx: -1 },
    secSeriesIndices: { seriesIdx: -1, sliceIdx: -1, imageIdx: -1 },
    PatHeight: 0,
    PatWeight: 0,
    BSA: 0,
    excludedImageUids: [],
  };
}

function calculateConeHeight(
  basalEndPoint1: Point,
  basalEndPoint2: Point,
  basalTipPoint: Point,
  baseRadius: number,
): number {
  // get long and short diagonal basal end points to tip
  const coneDiagonal1 = Math.sqrt(
    (basalEndPoint1.x - basalTipPoint.x) ** 2
    + (basalEndPoint1.y - basalTipPoint.y) ** 2,
  );
  const coneDiagonal2 = Math.sqrt(
    (basalEndPoint2.x - basalTipPoint.x) ** 2
    + (basalEndPoint2.y - basalTipPoint.y) ** 2,
  );
  const longerDiagonal
    = coneDiagonal1 > coneDiagonal2 ? coneDiagonal1 : coneDiagonal2;
  const shorterDiagonal
    = coneDiagonal1 <= coneDiagonal2 ? coneDiagonal1 : coneDiagonal2;

  // height of the cone
  const dist = (longerDiagonal ** 2 - shorterDiagonal ** 2) / (4 * baseRadius);
  const distDiff = dist - baseRadius;
  const height = Math.sqrt(longerDiagonal ** 2 - distDiff ** 2);

  return height;
}

function calculateConeVolume(
  basalEndPoint1: Point,
  basalEndPoint2: Point,
  basalPoint: Point,
): number {
  const baseRadius = Math.sqrt(
    ((basalEndPoint1.x - basalEndPoint2.x) / 2) ** 2
    + ((basalEndPoint1.y - basalEndPoint2.y) / 2) ** 2,
  );
  const height = calculateConeHeight(
    basalEndPoint1,
    basalEndPoint2,
    basalPoint,
    baseRadius,
  );
  const coneBaseArea = Math.PI * baseRadius ** 2;
  // cone volume: V = 1/3 B h
  const volume = (coneBaseArea * height) / 3.0;
  return volume;
}

function integrateSaxVolume(
  areas: number[],
  locs: number[],
  sliceThickness: number,
): number {
  if (areas.length === 0) return 0;
  if (areas.length === 1) return areas[0] * sliceThickness;
  let vol = 0;
  for (let v = 0; v < areas.length; v++) {
    if (v === 0) {
      vol += (areas[v] * sliceThickness) / 2;
      vol += (areas[v] * abs(locs[v] - locs[v + 1])) / 2;
    }
    else if (v === areas.length - 1) {
      vol += (areas[v] * sliceThickness) / 2;
      vol += (areas[v] * abs(locs[v] - locs[v - 1])) / 2;
    }
    else {
      vol += (areas[v] * abs(locs[v] - locs[v - 1])) / 2;
      vol += (areas[v] * abs(locs[v] - locs[v + 1])) / 2;
    }
  }
  return vol;
}

// Stamp per-image shortening colors onto the SAX chord + endo/epi (0%/100%
// transmurality) contour instances. Per-metric domains (mean±2std).
function applySaxShorteningColors(
  analysisState: FunctionAnalysisState,
  colormap: ColormapName,
) {
  const currentStudyStore = useCurrentStudyStore();
  const contourStore = useContourContainerStore();
  const series
    = currentStudyStore.activeStudy.series[analysisState.seriesIndices.seriesIdx];
  if (!series?.slices) return;

  const chordVals: number[] = [];
  const endoVals: number[] = [];
  const epiVals: number[] = [];
  analysisState.wallThickeningPerImage.forEach(pp =>
    pp.forEach(pc => pc.forEach(v => chordVals.push(v))));
  analysisState.SaxCFSEndoPerImage.forEach(r => r.forEach(v => endoVals.push(v)));
  analysisState.SaxCFSEpiPerImage.forEach(r => r.forEach(v => epiVals.push(v)));

  const chordColor = createColorScale(chordVals, colormap);
  const endoColor = createColorScale(endoVals, colormap, undefined, true);
  const epiColor = createColorScale(epiVals, colormap, undefined, true);

  for (let s = 0; s < series.slices.length; s++) {
    const slice = series.slices[s];
    if (!slice?.images) continue;
    for (let p = 0; p < slice.images.length; p++) {
      const imageId = slice.images[p]?.uid;
      if (!imageId) continue;
      const contours = contourStore.getContourContainer(imageId);
      if (!contours) continue;

      for (let c = 0; c < CHORD_NB; c++) {
        const chord = contours.getContour({
          label: ContourLabel.SaxLvCenterChord,
          id: encodeContourLabelItemId([
            CHORD_ENDO_OFFSET,
            CHORD_EPI_OFFSET,
            CHORD_NB,
            c,
          ]),
        });
        const col = chordColor(analysisState.wallThickeningPerImage[s]?.[p]?.[c]);
        if (chord && col) chord.getProps().color = col;
      }

      const endo = contours.getContour({
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([0]),
      });
      const endoCol = endoColor(analysisState.SaxCFSEndoPerImage[p]?.[s]);
      if (endo && endoCol) endo.getProps().color = endoCol;

      const epi = contours.getContour({
        label: ContourLabel.SaxLvTransmurality,
        id: encodeContourLabelItemId([100]),
      });
      const epiCol = epiColor(analysisState.SaxCFSEpiPerImage[p]?.[s]);
      if (epi && epiCol) epi.getProps().color = epiCol;
    }
  }
}

// LAX equivalent (single slice; endo/epi use longitudinal FS per frame).
function applyLaxShorteningColors(
  analysisState: FunctionAnalysisState,
  colormap: ColormapName,
) {
  const currentStudyStore = useCurrentStudyStore();
  const contourStore = useContourContainerStore();
  const { seriesIdx, sliceIdx } = analysisState.seriesIndices;
  const slice = currentStudyStore.activeStudy.series[seriesIdx]?.slices[sliceIdx];
  if (!slice?.images) return;

  const chordVals: number[] = [];
  analysisState.laxWallThickeningPerImage.forEach(pc =>
    pc.forEach(v => chordVals.push(v)));

  const chordColor = createColorScale(chordVals, colormap);
  const extendEndoColor = createColorScale(analysisState.LaxExtendLFSEndoPerImage, colormap, undefined, true);
  const extendEpiColor = createColorScale(analysisState.LaxExtendLFSEpiPerImage, colormap, undefined, true);
  const endoColor = createColorScale(analysisState.LaxLFSEndoPerImage, colormap, undefined, true);
  const epiColor = createColorScale(analysisState.LaxLFSEpiPerImage, colormap, undefined, true);

  for (let p = 0; p < slice.images.length; p++) {
    const imageId = slice.images[p]?.uid;
    if (!imageId) continue;
    const contours = contourStore.getContourContainer(imageId);
    if (!contours) continue;

    for (let c = 0; c < LAX_CHORD_NB; c++) {
      const chord = contours.getContour({
        label: ContourLabel.LaxLvCenterChord,
        id: encodeContourLabelItemId([
          CHORD_ENDO_OFFSET,
          CHORD_EPI_OFFSET,
          LAX_CHORD_NB,
          c,
        ]),
      });
      const col = chordColor(analysisState.laxWallThickeningPerImage[p]?.[c]);
      if (chord && col) chord.getProps().color = col;
    }

    const endo = contours.getContour({
      label: ContourLabel.LaxLvTransmurality,
      id: encodeContourLabelItemId([0]),
    });
    const endoCol = endoColor(analysisState.LaxLFSEndoPerImage[p]);
    if (endo && endoCol) endo.getProps().color = endoCol;

    const epi = contours.getContour({
      label: ContourLabel.LaxLvTransmurality,
      id: encodeContourLabelItemId([100]),
    });
    const epiCol = epiColor(analysisState.LaxLFSEpiPerImage[p]);
    if (epi && epiCol) epi.getProps().color = epiCol;

    const endoExtend = contours.getContour({
      label: ContourLabel.LaxLvExtendEndo,
      id: encodeContourLabelItemId([0]),
    });
    const endoExtendCol = extendEndoColor(analysisState.LaxExtendLFSEndoPerImage[p]);
    if (endoExtend && endoExtendCol) endoExtend.getProps().color = endoExtendCol;

    const epiExtend = contours.getContour({
      label: ContourLabel.LaxLvExtendEpi,
      id: encodeContourLabelItemId([0]),
    });
    const epiExtendCol = extendEpiColor(analysisState.LaxExtendLFSEpiPerImage[p]);
    if (epiExtend && epiExtendCol) epiExtend.getProps().color = epiExtendCol;
  }
}

interface FunctionCalculatorState {
  functionAnalysisStates: Record<string, FunctionAnalysisState>;
  isSAX: boolean;
  isSaxUserSelected: boolean;
  autoOrientation: boolean;
  showShortening: boolean;
  shorteningColormap: ColormapName;
}
export const useFunctionCalculatorStore = defineStore('functionCalculatorStore', {
  state: (): FunctionCalculatorState => ({
    functionAnalysisStates: {},
    isSAX: true,
    isSaxUserSelected: true,
    autoOrientation: true,
    showShortening: false,
    shorteningColormap: 'plasma',
  }),
  getters: {
    excludedImageUidsSet(state): Set<string> {
      const states = state.functionAnalysisStates;
      return new Set([
        ...(states.SaxStack?.excludedImageUids ?? []),
        ...(states.SaxStackPap?.excludedImageUids ?? []),
      ]);
    },
  },
  actions: {
    updateMain() {
      const classification = seriesClassification.getClassificationByViewport('FunctionMainView');
      if (classification?.orientation === Orientation.Sax) {
        this.$state.isSAX = true;
      }
      else {
        this.$state.isSAX = false;
      }

      this.updateAnalysisStates();
      this.calculate();
    },
    updateSecondary() {
      this.updateAnalysisStates();
      this.calculate();
    },
    updateAnalysisStates() {
      const mainViewportIndices
        = useViewportStateStore().getSelectedImageIndices('FunctionMainView');
      const secondaryViewportIndices
        = useViewportStateStore().getSelectedImageIndices(
          'FunctionSecondaryView',
        );

      this.$state.functionAnalysisStates = {};
      this.$state.functionAnalysisStates.SaxStack
        = getDefaultFunctionAnalysisState();
      this.$state.functionAnalysisStates.SaxStackPap
        = getDefaultFunctionAnalysisState();
      this.$state.functionAnalysisStates.LaxMonoplane2CV
        = getDefaultFunctionAnalysisState();
      this.$state.functionAnalysisStates.LaxMonoplane4CV
        = getDefaultFunctionAnalysisState();
      this.$state.functionAnalysisStates.LaxBiplane
        = getDefaultFunctionAnalysisState();
      if (this.getIsSaxAnalysis()) {
        this.$state.functionAnalysisStates.SaxStack.seriesIndices
          = mainViewportIndices;
        this.$state.functionAnalysisStates.SaxStack.secSeriesIndices
          = secondaryViewportIndices;
        this.$state.functionAnalysisStates.SaxStackPap.seriesIndices
          = mainViewportIndices;
        this.$state.functionAnalysisStates.SaxStackPap.secSeriesIndices
          = secondaryViewportIndices;
      }
      else {
        this.$state.functionAnalysisStates.LaxMonoplane2CV.seriesIndices
          = mainViewportIndices;
        this.$state.functionAnalysisStates.LaxMonoplane4CV.seriesIndices
          = secondaryViewportIndices;
        this.$state.functionAnalysisStates.LaxBiplane.seriesIndices
          = mainViewportIndices;
        this.$state.functionAnalysisStates.LaxBiplane.secSeriesIndices
          = secondaryViewportIndices;
      }
    },
    getIsSaxAnalysis() {
      if (this.$state.autoOrientation) {
        return this.$state.isSAX;
      }
      return this.$state.isSaxUserSelected;
    },
    resetAnalysisCalculations(analysisState: FunctionAnalysisState) {
      if (!analysisState) {
        return analysisState;
      }
      const { seriesIndices } = analysisState;
      const { secSeriesIndices } = analysisState;

      analysisState = getDefaultFunctionAnalysisState();
      analysisState.seriesIndices = seriesIndices;
      analysisState.secSeriesIndices = secSeriesIndices;

      return analysisState;
    },
    setShowShortening(show: boolean) {
      this.showShortening = show;
      this.calculate();
    },
    calculate() {
      if (this.getIsSaxAnalysis()) {
        const analysisStateSax = this.$state.functionAnalysisStates.SaxStack;
        this.$state.functionAnalysisStates.SaxStack
          = this.calculateSax(analysisStateSax);
      }
      else {
        const analysisStateMonoplane2CV
          = this.$state.functionAnalysisStates.LaxMonoplane2CV;
        this.$state.functionAnalysisStates.LaxMonoplane2CV
          = this.calculateMonoplaneLax(analysisStateMonoplane2CV);
        const analysisStateMonoplane4CV
          = this.$state.functionAnalysisStates.LaxMonoplane4CV;
        this.$state.functionAnalysisStates.LaxMonoplane4CV
          = this.calculateMonoplaneLax(analysisStateMonoplane4CV);
        const analysisStateBiplane
          = this.$state.functionAnalysisStates.LaxBiplane;
        this.$state.functionAnalysisStates.LaxBiplane
          = this.calculateBiplaneLax(analysisStateBiplane);
      }
    },
    calculateSax(analysisState: FunctionAnalysisState) {
      const currentStudyStore = useCurrentStudyStore();
      // v = sum(sliceLocDif * area))

      analysisState = this.resetAnalysisCalculations(analysisState);

      if (
        analysisState.seriesIndices.seriesIdx < 0
        || analysisState.seriesIndices.sliceIdx < 0
      ) {
        return analysisState;
      }
      const seriesIndex = analysisState.seriesIndices.seriesIdx;
      const sliceIndex = analysisState.seriesIndices.sliceIdx;

      const series = currentStudyStore.activeStudy.series[seriesIndex];
      const slice = series.slices[sliceIndex];

      const phaseCount = slice.images.length;
      const sliceCount = series.slices.length;

      let imageId = slice.images[0].uid;
      const imageInfo = currentStudyStore.imageInfoDicom[imageId];
      const sliceThickness = imageInfo.SliceThickness ?? 0;

      // LAX range intervals
      const laxSeriesIdx = analysisState.secSeriesIndices.seriesIdx;
      const laxSliceIdx = analysisState.secSeriesIndices.sliceIdx;
      const rangeIntervals
        = (laxSeriesIdx >= 0 && laxSliceIdx >= 0)
          ? computeSaxRangeIntervals(series, laxSeriesIdx, laxSliceIdx, phaseCount)
          : Array.from<[number, number] | null>({ length: phaseCount }).fill(null);

      analysisState.excludedImageUids = [];

      let sumHR = 0;
      let countHR = 0;
      analysisState.PatHeight = currentStudyStore.studyInfoDicom.PatientSize;
      analysisState.PatWeight = currentStudyStore.studyInfoDicom.PatientWeight;
      analysisState.BSA = currentStudyStore.getPatientBSA() ?? 0;

      for (let p = 0; p < phaseCount; p++) {
        const endoAreas = [];
        const excludedAreas = [];
        const myoAreas = [];
        const papAreas = [];
        const endoLocs = [];
        const excludedLocs = [];
        const myoLocs = [];
        const papLocs = [];
        const rvEndoAreas = [];
        const rvEndoLocs = [];
        const rvPapAreas = [];
        const rvPapLocs = [];

        let volume = 0;
        let papMass = 0;
        let excludedVolume = 0;
        let rvVolume = 0;
        let rvPapVolume = 0;
        let mass = 0;

        let sumTriggerTime = 0;
        let countTriggerTime = 0;

        const saxContourLengthEndo = [];
        const saxContourLengthEpi = [];

        for (let s = 0; s < sliceCount; s++) {
          const sliceSax = series.slices[s];

          if (!sliceSax.images[p]) continue;
          imageId = sliceSax.images[p].uid;
          const sliceImageInfo = currentStudyStore.imageInfoDicom[imageId];
          if (!sliceImageInfo) continue;

          const sliceLoc = sliceImageInfo.CalculatedSliceLocation;

          if (sliceImageInfo.TriggerTime) {
            sumTriggerTime += sliceImageInfo.TriggerTime;
            countTriggerTime++;
          }
          if (sliceImageInfo.CalculatedHeartRate) {
            sumHR += sliceImageInfo.CalculatedHeartRate;
            countHR++;
          }

          const iv = rangeIntervals[p];
          if (iv && (sliceLoc < iv[0] || sliceLoc > iv[1])) {
            analysisState.excludedImageUids.push(imageId);
            continue; // excluded from areas / mass / volume / chords for this phase
          }

          const contours
            = useContourContainerStore().getContourContainer(imageId);
          if (contours) {
            const endoContour = contours.getContour({
              label: ContourLabel.SaxLvEndo,
              id: encodeContourLabelItemId([0]),
            }) as PolygonClosedContour;
            const epiContour = contours.getContour({
              label: ContourLabel.SaxLvEpi,
              id: encodeContourLabelItemId([0]),
            }) as PolygonClosedContour;
            const myoContour = contours.getContour({
              label: ContourLabel.SaxLvMyoFusion,
              id: encodeContourLabelItemId([0, 100]),
            }) as AreaContourBase;
            const rvEndoContour = contours.getContour({
              label: ContourLabel.SaxRvEndo,
              id: encodeContourLabelItemId([0]),
            }) as AreaContourBase;
            if (endoContour) {
              endoAreas.push(endoContour.getAreaRealWorldvalue());
              saxContourLengthEndo.push(endoContour.getConvexHullSplineLength().hullPerimeter);
              endoLocs.push(sliceLoc);
            }
            else {
              saxContourLengthEndo.push(0);
            }
            if (epiContour) {
              saxContourLengthEpi.push(epiContour.getConvexHullSplineLength().hullPerimeter);
            }
            else {
              saxContourLengthEpi.push(0);
            }
            if (myoContour) {
              myoAreas.push(myoContour.getAreaRealWorldvalue());
              myoLocs.push(sliceLoc);
            }
            if (rvEndoContour) {
              rvEndoAreas.push(rvEndoContour.getAreaRealWorldvalue());
              rvEndoLocs.push(sliceLoc);
            }

            const drawing = contours.getDrawingContours();

            // papillary muscles (multiplicity 25 – sum all instances)
            const papMap = drawing?.[ContourLabel.SaxLvPapillary];
            if (papMap) {
              let papArea = 0;
              for (const c of Object.values(papMap)) {
                papArea += (c as PolygonClosedContour).getAreaRealWorldvalue();
              }
              if (papArea > 0) {
                papAreas.push(papArea);
                papLocs.push(sliceLoc);
              }
            }

            // user exclusion contour(s) (multiplicity 5 – independent of range)
            const exclusionMap = drawing?.[ContourLabel.SaxLvExclusion];
            if (exclusionMap) {
              let exclArea = 0;
              for (const c of Object.values(exclusionMap)) {
                exclArea += (c as PolygonClosedContour).getAreaRealWorldvalue();
              }
              if (exclArea > 0) {
                excludedAreas.push(exclArea);
                excludedLocs.push(sliceLoc);
              }
            }

            // RV papillary muscles (sum all instances)
            const rvPapMap = drawing?.[ContourLabel.SaxRvPapillary];
            if (rvPapMap) {
              let rvPapArea = 0;
              for (const c of Object.values(rvPapMap)) {
                rvPapArea += (c as PolygonClosedContour).getAreaRealWorldvalue();
              }
              if (rvPapArea > 0) {
                rvPapAreas.push(rvPapArea);
                rvPapLocs.push(sliceLoc);
              }
            }

            const chords = [];
            if (myoContour) {
              for (let c = 0; c < CHORD_NB; c++) {
                let chordLength = 0;
                const chordContour = contours.getContour({
                  label: ContourLabel.SaxLvCenterChord,
                  id: encodeContourLabelItemId([
                    CHORD_ENDO_OFFSET,
                    CHORD_EPI_OFFSET,
                    CHORD_NB,
                    c,
                  ]),
                }) as LineContour;

                if (chordContour) {
                  chordLength = chordContour.getDistanceRealWorldvalue();
                }
                chords.push(chordLength);
              }
            }
            analysisState.chords[s] = analysisState.chords[s] || [];
            analysisState.chords[s].push(chords);
          }
          else {
            // ToDo review error handling
            analysisState.chords[s] = analysisState.chords[s] || [];
            analysisState.chords[s].push([]);
            saxContourLengthEndo.push(0);
            saxContourLengthEpi.push(0);
          }
        }
        analysisState.SaxContourLengthEndo.push(saxContourLengthEndo);
        analysisState.SaxContourLengthEpi.push(saxContourLengthEpi);

        if (countTriggerTime > 0) {
          analysisState.triggerTimes.push(sumTriggerTime / countTriggerTime);
        }
        else {
          analysisState.triggerTimes.push(0);
        }

        volume = integrateSaxVolume(endoAreas, endoLocs, sliceThickness) / 1000;
        mass = integrateSaxVolume(myoAreas, myoLocs, sliceThickness) / 1000;
        rvVolume = integrateSaxVolume(rvEndoAreas, rvEndoLocs, sliceThickness) / 1000;
        rvPapVolume = integrateSaxVolume(rvPapAreas, rvPapLocs, sliceThickness) / 1000;
        papMass
          = integrateSaxVolume(papAreas, papLocs, sliceThickness) / 1000;
        excludedVolume
          = integrateSaxVolume(excludedAreas, excludedLocs, sliceThickness) / 1000;

        mass *= MYOCARDIAL_DENSITY;

        volume -= papMass; // papillary muscle out of LV blood pool
        papMass *= MYOCARDIAL_DENSITY;
        volume -= excludedVolume; // user exclusion contour(s)

        rvVolume -= rvPapVolume; // RV papillary muscles out of LV blood pool

        if (volume < 0) volume = 0;
        mass += papMass; // pap counted as myocardium

        if (rvVolume < 0) {
          rvVolume = 0;
        }

        analysisState.papMass.push(papMass);
        analysisState.excludedVolumes.push(excludedVolume);

        if (volume > 0) {
          if (volume > analysisState.EDV) {
            analysisState.diastolicPhase = p;
            analysisState.EDV = volume;
          }
          if (volume < analysisState.ESV) {
            analysisState.systolicPhase = p;
            analysisState.ESV = volume;
          }
        }

        analysisState.volumes.push(volume);
        analysisState.myoMass.push(mass);

        if (rvVolume > 0) {
          if (rvVolume > analysisState.RVEDV) {
            analysisState.diastolicPhaseRV = p;
            analysisState.RVEDV = rvVolume;
          }
          if (rvVolume < analysisState.RVESV) {
            analysisState.systolicPhaseRV = p;
            analysisState.RVESV = rvVolume;
          }
        }

        analysisState.rvVolumes.push(rvVolume);
        analysisState.rvPapVolumes.push(rvPapVolume);
      }
      if (
        analysisState.diastolicPhase >= 0
        && analysisState.systolicPhase >= 0
      ) {
        analysisState.SV = analysisState.EDV - analysisState.ESV;
        analysisState.EF = (analysisState.SV / analysisState.EDV) * 100;
        analysisState.systolicMyoMass
          = analysisState.myoMass[analysisState.systolicPhase];
        analysisState.diastolicMyoMass
          = analysisState.myoMass[analysisState.diastolicPhase];
        analysisState.systolicPapMass
          = analysisState.papMass[analysisState.systolicPhase];
        analysisState.diastolicPapMass
          = analysisState.papMass[analysisState.diastolicPhase];
      }

      if (
        analysisState.diastolicPhaseRV >= 0
        && analysisState.systolicPhaseRV >= 0
      ) {
        analysisState.RVSV = analysisState.RVEDV - analysisState.RVESV;
        analysisState.RVEF = (analysisState.RVSV / analysisState.RVEDV) * 100;
        analysisState.systolicRvPapVolume
          = analysisState.rvPapVolumes[analysisState.systolicPhaseRV];
        analysisState.diastolicRvPapVolume
          = analysisState.rvPapVolumes[analysisState.diastolicPhaseRV];
      }

      // wall thickening
      for (let s = 0; s < sliceCount; s++) {
        analysisState.wallThickening[s] = analysisState.wallThickening[s] || [];
        analysisState.wallThickeningPerImage[s] = analysisState.wallThickeningPerImage[s] || [];
        for (let c = 0; c < CHORD_NB; c++) {
          const chordLengthDiastolic
            = analysisState.chords[s]?.[analysisState.diastolicPhase]?.[c];
          const chordLengthSystolic
            = analysisState.chords[s]?.[analysisState.systolicPhase]?.[c];

          if (
            chordLengthDiastolic
            && chordLengthSystolic
            && chordLengthDiastolic !== 0
          ) {
            analysisState.wallThickening[s].push(
              ((chordLengthSystolic - chordLengthDiastolic)
                / chordLengthDiastolic)
              * 100,
            );
          }
          else {
            analysisState.wallThickening[s].push(Number.NaN);
          }
        }

        // per-image (per-phase) thickening vs end-diastole
        for (let p = 0; p < phaseCount; p++) {
          analysisState.wallThickeningPerImage[s][p] = [];
          for (let c = 0; c < CHORD_NB; c++) {
            const chordLengthDiastolic
              = analysisState.chords[s]?.[analysisState.diastolicPhase]?.[c];
            const chordLengthPhase = analysisState.chords[s]?.[p]?.[c];
            const wt
              = chordLengthDiastolic && chordLengthPhase && chordLengthDiastolic !== 0
                ? ((chordLengthPhase - chordLengthDiastolic) / chordLengthDiastolic) * 100
                : Number.NaN;
            analysisState.wallThickeningPerImage[s][p].push(wt);
          }
        }
      }

      // CFS: circumferential fractional shortening
      let countCFSEndo = 0;
      let countCFSEpi = 0;
      for (let s = 0; s < sliceCount; s++) {
        const lengthEndoDiastolic = analysisState.SaxContourLengthEndo[analysisState.diastolicPhase]?.[s] ?? 0;
        const lengthEndoSystolic = analysisState.SaxContourLengthEndo[analysisState.systolicPhase]?.[s] ?? 0;
        const lengthEpiDiastolic = analysisState.SaxContourLengthEpi[analysisState.diastolicPhase]?.[s] ?? 0;
        const lengthEpiSystolic = analysisState.SaxContourLengthEpi[analysisState.systolicPhase]?.[s] ?? 0;
        if (lengthEndoDiastolic > 0 && lengthEndoSystolic > 0) {
          analysisState.CFSEndo += (lengthEndoSystolic - lengthEndoDiastolic) / lengthEndoDiastolic;
          countCFSEndo++;
        }

        if (lengthEpiDiastolic > 0 && lengthEpiSystolic > 0) {
          analysisState.CFSEpi += (lengthEpiSystolic - lengthEpiDiastolic) / lengthEpiDiastolic;
          countCFSEpi++;
        }
      }
      if (countCFSEndo > 0) {
        analysisState.CFSEndo /= countCFSEndo;
      }
      if (countCFSEpi > 0) {
        analysisState.CFSEpi /= countCFSEpi;
      }

      analysisState.CFSEndo *= 100;
      analysisState.CFSEpi *= 100;

      // per-image circumferential shortening vs end-diastole
      for (let p = 0; p < phaseCount; p++) {
        analysisState.SaxCFSEndoPerImage[p] = [];
        analysisState.SaxCFSEpiPerImage[p] = [];
        for (let s = 0; s < sliceCount; s++) {
          const endoDiastolic
            = analysisState.SaxContourLengthEndo[analysisState.diastolicPhase]?.[s] ?? 0;
          const endoPhase = analysisState.SaxContourLengthEndo[p]?.[s] ?? 0;
          const epiDiastolic
            = analysisState.SaxContourLengthEpi[analysisState.diastolicPhase]?.[s] ?? 0;
          const epiPhase = analysisState.SaxContourLengthEpi[p]?.[s] ?? 0;

          analysisState.SaxCFSEndoPerImage[p][s]
            = (endoDiastolic > 0 && endoPhase > 0)
              ? ((endoPhase - endoDiastolic) / endoDiastolic) * 100
              : Number.NaN;
          analysisState.SaxCFSEpiPerImage[p][s]
            = (epiDiastolic > 0 && epiPhase > 0)
              ? ((epiPhase - epiDiastolic) / epiDiastolic) * 100
              : Number.NaN;
        }
      }

      if (countHR > 0) {
        analysisState.HeartRateMean = sumHR / countHR;
        analysisState.CO = analysisState.SV * analysisState.HeartRateMean / 1000;
      }

      if (analysisState.systolicPhase < 0 || !Number.isFinite(analysisState.ESV)) {
        analysisState.ESV = 0;
      }
      if (analysisState.systolicPhaseRV < 0 || !Number.isFinite(analysisState.RVESV)) {
        analysisState.RVESV = 0;
      }

      if (analysisState.BSA > 0) {
        analysisState.CI = analysisState.CO / analysisState.BSA;
        analysisState.SVindBSA = analysisState.SV / analysisState.BSA;
        analysisState.EDVindBSA = analysisState.EDV / analysisState.BSA;
        analysisState.ESVindBSA = analysisState.ESV / analysisState.BSA;
        analysisState.RVSVindBSA = analysisState.RVSV / analysisState.BSA;
        analysisState.RVEDVindBSA = analysisState.RVEDV / analysisState.BSA;
        analysisState.RVESVindBSA = analysisState.RVESV / analysisState.BSA;
        analysisState.systolicMyoMassindBSA
          = analysisState.systolicMyoMass / analysisState.BSA;
        analysisState.diastolicMyoMassindBSA
          = analysisState.diastolicMyoMass / analysisState.BSA;
      }

      if (analysisState.PatHeight > 0) {
        analysisState.SVindH = analysisState.SV / analysisState.PatHeight;
        analysisState.EDVindH = analysisState.EDV / analysisState.PatHeight;
        analysisState.ESVindH = analysisState.ESV / analysisState.PatHeight;
        analysisState.RVSVindH = analysisState.RVSV / analysisState.PatHeight;
        analysisState.RVEDVindH = analysisState.RVEDV / analysisState.PatHeight;
        analysisState.RVESVindH = analysisState.RVESV / analysisState.PatHeight;
        analysisState.systolicMyoMassindH
          = analysisState.systolicMyoMass / analysisState.PatHeight;
        analysisState.diastolicMyoMassindH
          = analysisState.diastolicMyoMass / analysisState.PatHeight;
      }

      // end of calculateSax
      if (this.showShortening) {
        applySaxShorteningColors(analysisState, this.shorteningColormap);
      }
      return analysisState;
    },
    calculateMonoplaneLax(analysisState: FunctionAnalysisState) {
      const currentStudyStore = useCurrentStudyStore();
      // v = (8*a*a)/(3*pi*l)

      analysisState = this.resetAnalysisCalculations(analysisState);
      if (
        analysisState.seriesIndices.seriesIdx < 0
        || analysisState.seriesIndices.sliceIdx < 0
      ) {
        return analysisState;
      }
      const seriesIndex = analysisState.seriesIndices.seriesIdx;
      const sliceIndex = analysisState.seriesIndices.sliceIdx;

      const series = currentStudyStore.activeStudy.series[seriesIndex];
      if (!series || !series.slices) {
        console.warn(`Series not found at index ${seriesIndex}`);
        return analysisState;
      }
      const slice = series.slices[sliceIndex];
      if (!slice || !slice.images) {
        console.warn(
          `Slice not found at index ${sliceIndex} in series ${seriesIndex}`,
        );
        return analysisState;
      }

      let sumHR = 0;
      let countHR = 0;
      analysisState.PatHeight = currentStudyStore.studyInfoDicom.PatientSize;
      analysisState.PatWeight = currentStudyStore.studyInfoDicom.PatientWeight;
      analysisState.BSA = currentStudyStore.getPatientBSA() ?? 0;

      const phaseCount = slice.images.length;
      for (let p = 0; p < phaseCount; p++) {
        const imageId = slice.images[p].uid;
        const imageInfo = currentStudyStore.imageInfoDicom[imageId];
        if (!imageInfo) continue;

        let volume = 0;
        let mass = 0;
        let lengthEndo = 0;
        let lengthEpi = 0;
        let basalConeVolume = 0;
        let volumeWithCone = 0;
        let areaEndo = 0;
        let areaEpi = 0;
        const triggerTime = imageInfo.TriggerTime ?? 0;
        let laxContourLengthEndo = 0;
        let laxContourLengthEpi = 0;

        if (imageInfo.CalculatedHeartRate) {
          sumHR += imageInfo.CalculatedHeartRate;
          countHR++;
        }

        const contours
          = useContourContainerStore().getContourContainer(imageId);
        const laxChordLengths = Array.from({ length: LAX_CHORD_NB }).fill(0) as number[];
        if (contours) {
          const endoContour = contours.getContour({
            label: ContourLabel.LaxLvEndo,
            id: encodeContourLabelItemId([0]),
          }) as PolygonOpenContour;
          const extendEndoContour = contours.getContour({
            label: ContourLabel.LaxLvExtendEndo,
            id: encodeContourLabelItemId([0]),
          }) as LineContour;
          const basalPoint = contours.getContour({
            label: ContourLabel.LaxLvEndoBasalPoint,
            id: encodeContourLabelItemId([0]),
          }) as PointContour;
          if (endoContour && extendEndoContour) {
            const area = endoContour.getAreaRealWorldvalue();
            areaEndo = area;
            lengthEndo = extendEndoContour.getDistanceRealWorldvalue();

            volume = (8 * area * area) / (3 * Math.PI * lengthEndo);
          }
          if (endoContour && basalPoint) {
            const endoPolygon = endoContour.getPolygonScaled();
            const basalTipPoint = basalPoint.getPolygonScaled()[0];
            basalConeVolume = calculateConeVolume(
              endoPolygon[0],
              endoPolygon[endoPolygon.length - 1],
              basalTipPoint,
            );
            const pixelSpacing = endoContour.getPixelSpacing();
            const pixelSize = pixelSpacing[0] * pixelSpacing[1];
            const pixelVolume
              = (pixelSize * (pixelSpacing[0] + pixelSpacing[1])) / 2;
            basalConeVolume *= pixelVolume;

            // check if basal point is inside
            const inside = isPointWithinPolygon(basalTipPoint, endoPolygon);
            if (inside) basalConeVolume *= -1;
          }
          if (endoContour) {
            laxContourLengthEndo = endoContour.getConvexHullSplineLength().hullPerimeter;
          }
          const epiContour = contours.getContour({
            label: ContourLabel.LaxLvEpi,
            id: encodeContourLabelItemId([0]),
          }) as PolygonOpenContour;
          const extendEpiContour = contours.getContour({
            label: ContourLabel.LaxLvExtendEpi,
            id: encodeContourLabelItemId([0]),
          }) as LineContour;
          if (epiContour && extendEpiContour) {
            const area = epiContour.getAreaRealWorldvalue();
            areaEpi = area;
            lengthEpi = extendEpiContour.getDistanceRealWorldvalue();
            if (volume > 0) {
              mass = (8 * area * area) / (3 * Math.PI * lengthEpi) - volume;
              mass *= MYOCARDIAL_DENSITY;
            }
          }
          if (epiContour) {
            laxContourLengthEpi = epiContour.getConvexHullSplineLength().hullPerimeter;
          }
          for (let c = 0; c < LAX_CHORD_NB; c++) {
            const chordContour = contours.getContour({
              label: ContourLabel.LaxLvCenterChord,
              id: encodeContourLabelItemId([
                CHORD_ENDO_OFFSET,
                CHORD_EPI_OFFSET,
                LAX_CHORD_NB,
                c,
              ]),
            }) as LineContour | undefined;
            if (chordContour?.isValid()) {
              laxChordLengths[c] = chordContour.getDistanceRealWorldvalue();
            }
            else {
              laxChordLengths[c] = 0;
            }
          }
        }

        volume /= 1000;
        mass /= 1000;
        basalConeVolume /= 1000;
        volumeWithCone = volume + basalConeVolume;

        if (volumeWithCone > 0) {
          if (volumeWithCone > analysisState.EDV) {
            analysisState.diastolicPhase = p;
            analysisState.EDV = volumeWithCone;
          }
          if (volumeWithCone < analysisState.ESV) {
            analysisState.systolicPhase = p;
            analysisState.ESV = volumeWithCone;
          }
        }

        analysisState.volumes.push(volume);
        analysisState.volumesWithCone.push(volumeWithCone);
        analysisState.basalConeVolumes.push(basalConeVolume);
        analysisState.myoMass.push(mass);
        analysisState.lengthEndo.push(lengthEndo);
        analysisState.lengthEpi.push(lengthEpi);
        analysisState.areaEndo.push(areaEndo);
        analysisState.areaEpi.push(areaEpi);
        analysisState.triggerTimes.push(triggerTime);
        analysisState.LaxContourLengthEndo.push(laxContourLengthEndo);
        analysisState.LaxContourLengthEpi.push(laxContourLengthEpi);
        analysisState.laxChords.push(laxChordLengths);
      }

      if (
        analysisState.diastolicPhase >= 0
        && analysisState.systolicPhase >= 0
      ) {
        analysisState.SV = analysisState.EDV - analysisState.ESV;
        analysisState.EF = (analysisState.SV / analysisState.EDV) * 100;

        if (
          analysisState.lengthEndo[analysisState.diastolicPhase] > 0
          && analysisState.lengthEndo[analysisState.systolicPhase] > 0
        ) {
          analysisState.GLSEndo
            = (analysisState.lengthEndo[analysisState.systolicPhase]
              - analysisState.lengthEndo[analysisState.diastolicPhase])
            / analysisState.lengthEndo[analysisState.diastolicPhase];
        }
        if (
          analysisState.lengthEpi[analysisState.diastolicPhase] > 0
          && analysisState.lengthEpi[analysisState.systolicPhase] > 0
        ) {
          analysisState.GLSEpi
            = (analysisState.lengthEpi[analysisState.systolicPhase]
              - analysisState.lengthEpi[analysisState.diastolicPhase])
            / analysisState.lengthEpi[analysisState.diastolicPhase];
        }

        analysisState.GLSEndo *= 100;
        analysisState.GLSEpi *= 100;

        analysisState.systolicMyoMass
          = analysisState.myoMass[analysisState.systolicPhase];
        analysisState.diastolicMyoMass
          = analysisState.myoMass[analysisState.diastolicPhase];
      }

      // LFS: longitudinal fractional shortening
      const lengthEndoDiastolic = analysisState.LaxContourLengthEndo[analysisState.diastolicPhase];
      const lengthEndoSystolic = analysisState.LaxContourLengthEndo[analysisState.systolicPhase];
      const lengthEpiDiastolic = analysisState.LaxContourLengthEpi[analysisState.diastolicPhase];
      const lengthEpiSystolic = analysisState.LaxContourLengthEpi[analysisState.systolicPhase];
      if (lengthEndoDiastolic > 0 && lengthEndoSystolic > 0) {
        analysisState.LFSEndo = (lengthEndoSystolic - lengthEndoDiastolic) / lengthEndoDiastolic;
      }

      if (lengthEpiDiastolic > 0 && lengthEpiSystolic > 0) {
        analysisState.LFSEpi = (lengthEpiSystolic - lengthEpiDiastolic) / lengthEpiDiastolic;
      }

      analysisState.LFSEndo *= 100;
      analysisState.LFSEpi *= 100;

      // per-image longitudinal shortening vs end-diastole
      for (let p = 0; p < phaseCount; p++) {
        const endoD = analysisState.LaxContourLengthEndo[analysisState.diastolicPhase] ?? 0;
        const endoP = analysisState.LaxContourLengthEndo[p] ?? 0;
        const epiD = analysisState.LaxContourLengthEpi[analysisState.diastolicPhase] ?? 0;
        const epiP = analysisState.LaxContourLengthEpi[p] ?? 0;
        analysisState.LaxLFSEndoPerImage[p]
          = (endoD > 0 && endoP > 0) ? ((endoP - endoD) / endoD) * 100 : Number.NaN;
        analysisState.LaxLFSEpiPerImage[p]
          = (epiD > 0 && epiP > 0) ? ((epiP - epiD) / epiD) * 100 : Number.NaN;

        // extend-line shortening vs ED (independent from FS contours)
        const exEndoD = analysisState.lengthEndo[analysisState.diastolicPhase] ?? 0;
        const exEndoP = analysisState.lengthEndo[p] ?? 0;
        const exEpiD = analysisState.lengthEpi[analysisState.diastolicPhase] ?? 0;
        const exEpiP = analysisState.lengthEpi[p] ?? 0;
        analysisState.LaxExtendLFSEndoPerImage[p]
          = (exEndoD > 0 && exEndoP > 0) ? ((exEndoP - exEndoD) / exEndoD) * 100 : Number.NaN;
        analysisState.LaxExtendLFSEpiPerImage[p]
          = (exEpiD > 0 && exEpiP > 0) ? ((exEpiP - exEpiD) / exEpiD) * 100 : Number.NaN;
      }

      // LAX wall thickening (LAX_CHORD_NB chords) vs end-diastole
      for (let c = 0; c < LAX_CHORD_NB; c++) {
        const cd = analysisState.laxChords[analysisState.diastolicPhase]?.[c] ?? 0;
        const cs = analysisState.laxChords[analysisState.systolicPhase]?.[c] ?? 0;
        analysisState.laxWallThickening[c]
          = cd > 0 && cs > 0 ? ((cs - cd) / cd) * 100 : Number.NaN;
      }
      for (let p = 0; p < phaseCount; p++) {
        analysisState.laxWallThickeningPerImage[p] = [];
        for (let c = 0; c < LAX_CHORD_NB; c++) {
          const cd = analysisState.laxChords[analysisState.diastolicPhase]?.[c] ?? 0;
          const cp = analysisState.laxChords[p]?.[c] ?? 0;
          analysisState.laxWallThickeningPerImage[p][c]
            = cd > 0 && cp ? ((cp - cd) / cd) * 100 : Number.NaN;
        }
      }

      if (countHR > 0) {
        analysisState.HeartRateMean = sumHR / countHR;
        analysisState.CO = analysisState.SV * analysisState.HeartRateMean / 1000;
      }

      if (analysisState.systolicPhase < 0 || !Number.isFinite(analysisState.ESV)) {
        analysisState.ESV = 0;
      }

      if (analysisState.BSA > 0) {
        analysisState.CI = analysisState.CO / analysisState.BSA;
        analysisState.SVindBSA = analysisState.SV / analysisState.BSA;
        analysisState.EDVindBSA = analysisState.EDV / analysisState.BSA;
        analysisState.ESVindBSA = analysisState.ESV / analysisState.BSA;
        analysisState.systolicMyoMassindBSA
          = analysisState.systolicMyoMass / analysisState.BSA;
        analysisState.diastolicMyoMassindBSA
          = analysisState.diastolicMyoMass / analysisState.BSA;
      }

      if (analysisState.PatHeight > 0) {
        analysisState.SVindH = analysisState.SV / analysisState.PatHeight;
        analysisState.EDVindH = analysisState.EDV / analysisState.PatHeight;
        analysisState.ESVindH = analysisState.ESV / analysisState.PatHeight;
        analysisState.systolicMyoMassindH
          = analysisState.systolicMyoMass / analysisState.PatHeight;
        analysisState.diastolicMyoMassindH
          = analysisState.diastolicMyoMass / analysisState.PatHeight;
      }

      // end of calculateLax
      if (this.showShortening) {
        applyLaxShorteningColors(analysisState, this.shorteningColormap);
      }
      return analysisState;
    },
    calculateBiplaneLax(analysisState: FunctionAnalysisState) {
      // v = 8*a1*a2/(3*pi*l)

      analysisState = this.resetAnalysisCalculations(analysisState);

      const state2CV = this.$state.functionAnalysisStates.LaxMonoplane2CV;
      const state4CV = this.$state.functionAnalysisStates.LaxMonoplane4CV;

      if (
        state2CV.seriesIndices.seriesIdx < 0
        && state4CV.seriesIndices.seriesIdx < 0
      ) {
        return analysisState;
      }

      const count2CV = state2CV.volumes.length;
      const count4CV = state4CV.volumes.length;

      if (count2CV !== count4CV) {
        return analysisState;
      }
      const phaseCount = count2CV;

      if (state2CV.EDV <= 0 || state4CV.EDV <= 0) {
        return analysisState;
      }

      for (let p = 0; p < phaseCount; p++) {
        let volume = 0;
        let basalConeVolume = 0;
        let volumeWithCone = 0;
        let mass = 0;
        let lengthEndo = 0;
        let lengthEpi = 0;

        const triggerTime2CV = state2CV.triggerTimes[p];
        const triggerTime4CV = state4CV.triggerTimes[p];
        const triggerTime = (triggerTime2CV + triggerTime4CV) / 2;

        const areaEndo2CV = state2CV.areaEndo[p];
        const areaEndo4CV = state4CV.areaEndo[p];

        const areaEpi2CV = state2CV.areaEpi[p];
        const areaEpi4CV = state4CV.areaEpi[p];

        const lengthEndo2CV = state2CV.lengthEndo[p];
        const lengthEndo4CV = state4CV.lengthEndo[p];

        const lengthEpi2CV = state2CV.lengthEpi[p];
        const lengthEpi4CV = state4CV.lengthEpi[p];

        const basalConeVolume2CV = state2CV.basalConeVolumes[p];
        const basalConeVolume4CV = state4CV.basalConeVolumes[p];

        basalConeVolume = (basalConeVolume2CV + basalConeVolume4CV) / 2;

        if (lengthEndo2CV > 0 && lengthEndo4CV > 0) {
          lengthEndo = (lengthEndo2CV + lengthEndo4CV) / 2;
        }
        if (lengthEpi2CV > 0 && lengthEpi4CV > 0) {
          lengthEpi = (lengthEpi2CV + lengthEpi4CV) / 2;
        }

        const lengthEndoCalc
          = lengthEndo2CV > lengthEndo4CV ? lengthEndo2CV : lengthEndo4CV;
        const lengthEpiCalc
          = lengthEpi2CV > lengthEpi4CV ? lengthEpi2CV : lengthEpi4CV;

        volume
          = (8 * areaEndo2CV * areaEndo4CV) / (3 * Math.PI * lengthEndoCalc);

        if (volume > 0) {
          mass
            = (8 * areaEpi2CV * areaEpi4CV) / (3 * Math.PI * lengthEpiCalc)
              - volume;
          mass *= MYOCARDIAL_DENSITY;
        }

        volume /= 1000;
        mass /= 1000;
        volumeWithCone = volume + basalConeVolume;

        if (volumeWithCone > 0) {
          if (volumeWithCone > analysisState.EDV) {
            analysisState.diastolicPhase = p;
            analysisState.EDV = volumeWithCone;
          }
          if (volumeWithCone < analysisState.ESV) {
            analysisState.systolicPhase = p;
            analysisState.ESV = volumeWithCone;
          }
        }

        analysisState.volumes.push(volume);
        analysisState.volumesWithCone.push(volumeWithCone);
        analysisState.myoMass.push(mass);
        analysisState.lengthEndo.push(lengthEndo);
        analysisState.lengthEpi.push(lengthEpi);
        analysisState.triggerTimes.push(triggerTime);
      }

      if (
        analysisState.diastolicPhase >= 0
        && analysisState.systolicPhase >= 0
      ) {
        analysisState.SV = analysisState.EDV - analysisState.ESV;
        analysisState.EF = (analysisState.SV / analysisState.EDV) * 100;

        if (
          analysisState.lengthEndo[analysisState.diastolicPhase] > 0
          && analysisState.lengthEndo[analysisState.systolicPhase] > 0
        ) {
          analysisState.GLSEndo
            = (analysisState.lengthEndo[analysisState.systolicPhase]
              - analysisState.lengthEndo[analysisState.diastolicPhase])
            / analysisState.lengthEndo[analysisState.diastolicPhase];
        }
        if (
          analysisState.lengthEpi[analysisState.diastolicPhase] > 0
          && analysisState.lengthEpi[analysisState.systolicPhase] > 0
        ) {
          analysisState.GLSEpi
            = (analysisState.lengthEpi[analysisState.systolicPhase]
              - analysisState.lengthEpi[analysisState.diastolicPhase])
            / analysisState.lengthEpi[analysisState.diastolicPhase];
        }

        analysisState.GLSEndo *= 100;
        analysisState.GLSEpi *= 100;

        analysisState.systolicMyoMass
          = analysisState.myoMass[analysisState.systolicPhase];
        analysisState.diastolicMyoMass
          = analysisState.myoMass[analysisState.diastolicPhase];
      }

      // LFS: longitudinal fractional shortening
      let lfsEndoSum = 0;
      let lfsEndoCount = 0;
      if (Number.isFinite(state2CV.LFSEndo) && state2CV.LFSEndo !== 0) {
        lfsEndoSum += state2CV.LFSEndo;
        lfsEndoCount++;
      }
      if (Number.isFinite(state4CV.LFSEndo) && state4CV.LFSEndo !== 0) {
        lfsEndoSum += state4CV.LFSEndo;
        lfsEndoCount++;
      }
      analysisState.LFSEndo = lfsEndoCount > 0 ? lfsEndoSum / lfsEndoCount : 0;

      let cfsEpiSum = 0;
      let cfsEpiCount = 0;
      if (Number.isFinite(state2CV.CFSEpi) && state2CV.CFSEpi !== 0) {
        cfsEpiSum += state2CV.CFSEpi;
        cfsEpiCount++;
      }
      if (Number.isFinite(state4CV.CFSEpi) && state4CV.CFSEpi !== 0) {
        cfsEpiSum += state4CV.CFSEpi;
        cfsEpiCount++;
      }
      analysisState.CFSEpi = cfsEpiCount > 0 ? cfsEpiSum / cfsEpiCount : 0;

      analysisState.LFSEndo *= 100;
      analysisState.LFSEpi *= 100;

      let hrSum = 0;
      let hrCount = 0;
      if (state2CV.HeartRateMean > 0) {
        hrSum += state2CV.HeartRateMean;
        hrCount++;
      }
      if (state4CV.HeartRateMean > 0) {
        hrSum += state4CV.HeartRateMean;
        hrCount++;
      }
      analysisState.HeartRateMean = hrCount > 0 ? hrSum / hrCount : 0;
      analysisState.CO = analysisState.SV * analysisState.HeartRateMean / 1000;

      if (analysisState.systolicPhase < 0 || !Number.isFinite(analysisState.ESV)) {
        analysisState.ESV = 0;
      }

      if (analysisState.BSA > 0) {
        analysisState.CI = analysisState.CO / analysisState.BSA;
        analysisState.SVindBSA = analysisState.SV / analysisState.BSA;
        analysisState.EDVindBSA = analysisState.EDV / analysisState.BSA;
        analysisState.ESVindBSA = analysisState.ESV / analysisState.BSA;
      }

      if (analysisState.PatHeight > 0) {
        analysisState.SVindH = analysisState.SV / analysisState.PatHeight;
        analysisState.EDVindH = analysisState.EDV / analysisState.PatHeight;
        analysisState.ESVindH = analysisState.ESV / analysisState.PatHeight;
      }

      return analysisState;
    },
  },
});
